import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getFocusClasses } from '@/lib/responsive-utils';

interface ServiceSubsection {
  title: string;
  subtitle?: string;
  items: string[];
}

interface ServiceSection {
  title: string;
  items?: string[];
  subsections?: ServiceSubsection[];
}

interface ServiceDetailProps {
  title: string;
  description: string;
  image: string;
  sections?: ServiceSection[];
}

export const ServiceDetailPage: React.FC<ServiceDetailProps> = ({
  title,
  description,
  image,
  sections
}) => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState<Set<number>>(new Set());

  const toggleSection = (index: number) => {
    setOpenSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

 const handleBackToServices = () => {
  // Primero navega a la página principal
  navigate('/');
  
  // Después de un pequeño delay, hace scroll a servicios
  setTimeout(() => {
    const serviciosSection = document.getElementById('servicios');
    if (serviciosSection) {
      const headerHeight = 80; // Altura del header fijo
      const targetPosition = serviciosSection.offsetTop ;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }, 100);
};
 const handleBackToContact = () => {
  // Primero navega a la página principal
  navigate('/');
  
  // Después de un pequeño delay, hace scroll a servicios
  setTimeout(() => {
    const cotizaSection = document.getElementById('cotiza');
    if (cotizaSection) {
      const headerHeight = 80; // Altura del header fijo
      const targetPosition = cotizaSection.offsetTop ;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }, 100);
};


  return (
    <div className="min-h-screen bg-white">
      {/* Main Content Container */}
      <div className="flex flex-col lg:flex-row h-screen">
        {/* Left Side - Fixed Image */}
        <div className="lg:w-1/2 h-64 lg:h-screen relative lg:fixed lg:left-0 lg:top-0">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>

        {/* Right Side - Scrollable Content */}
        <div className="lg:w-1/2 lg:ml-[50%] bg-white">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16">
            {/* Back Button */}
            <button
              onClick={handleBackToServices}
              className={cn(
                "inline-flex items-center gap-2 text-liec-primary-500 hover:text-liec-primary-600 font-semibold mb-6 transition-colors duration-200",
                getFocusClasses()
              )}
            >
              <ArrowLeft size={18} />
              <span className="text-sm uppercase tracking-wide">SERVICIOS</span>
            </button>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-liec-neutral-900 mb-6 leading-tight">
              {title}
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg text-liec-neutral-600 leading-relaxed mb-10">
              {description}
            </p>

            {/* Sections - Optional */}
            {sections && sections.length > 0 && (
              <div className="space-y-8">
                {sections.map((section, index) => {
                  const isOpen = openSections.has(index);
                  
                  return (
                    <div key={index} className="border-b border-liec-neutral-200 pb-6">
                      <button
                        onClick={() => toggleSection(index)}
                        className={cn(
                          "w-full flex items-center justify-between text-left group",
                          getFocusClasses()
                        )}
                        aria-expanded={isOpen}
                      >
                        <h2 className="text-xl sm:text-2xl font-semibold text-liec-neutral-900 group-hover:text-liec-primary-500 transition-colors duration-200">
                          {section.title}
                        </h2>
                        <svg
                          className={cn(
                            "w-5 h-5 text-liec-neutral-400 group-hover:text-liec-primary-500 transition-all duration-200",
                            isOpen ? "rotate-180" : "rotate-0"
                          )}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Section Content */}
                      {isOpen && (
                        <div className="mt-4 space-y-6">
                          {/* Direct items */}
                          {section.items && section.items.length > 0 && (
                            <ul className="space-y-2">
                              {section.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start gap-3">
                                  <span className="text-liec-primary-500 mt-1.5">•</span>
                                  <span className="text-base text-liec-neutral-700 leading-relaxed">
                                    {item}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}

                          {/* Subsections */}
                          {section.subsections && section.subsections.length > 0 && (
                            <div className="space-y-4 pl-4">
                              {section.subsections.map((subsection, subIndex) => (
                                <div key={subIndex}>
                                  <h3 className="text-lg font-semibold text-liec-neutral-800 mb-2">
                                    {subsection.title}
                                  </h3>
                                  {subsection.subtitle && (
                                    <p className="text-sm text-liec-neutral-600 mb-3">
                                      {subsection.subtitle}
                                    </p>
                                  )}
                                  <ul className="space-y-2">
                                    {subsection.items.map((item, itemIndex) => (
                                      <li key={itemIndex} className="flex items-start gap-3">
                                        <span className="text-liec-primary-400 mt-1.5">◦</span>
                                        <span className="text-base text-liec-neutral-700 leading-relaxed">
                                          {item}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* CTA Button */}
            <div className="mt-12 pt-8 border-t border-liec-neutral-200">
              <Button
                className={cn(
                  "bg-liec-primary-500 hover:bg-liec-primary-600 text-white font-semibold px-8 py-3",
                  "transition-all duration-200 shadow-md hover:shadow-lg",
                  getFocusClasses()
                )}
                onClick={handleBackToContact}
              >
                SOLICITAR COTIZACIÓN
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
